/**
 * <Skeleton /> — placeholder for known-layout content during load.
 *
 * Per DESIGN_SYSTEM.md §8 "Loading states (skeleton-first)":
 *   - Use for known layouts (table rows, cards, dashboard widgets).
 *   - Animated subtle shimmer.
 *   - Never show a blank screen; skeleton beats spinner for layout
 *     hydration.
 *
 * The shimmer is a CSS gradient that translates left-to-right over
 * --motion-medium; reduced-motion turns it static gray.
 */

import type { CSSProperties, ReactNode } from "react";

export interface SkeletonProps {
  /** Visual variant. Determines border-radius. */
  variant?: "rect" | "text" | "circle";
  /** Width — CSS length or percentage. Default "100%". */
  width?: number | string;
  /** Height — CSS length. Default "1rem" for text, "100%" otherwise. */
  height?: number | string;
  /** Additional Tailwind classes. */
  className?: string;
  /** Optional accessible label; default "Loading". */
  "aria-label"?: string;
}

function toCssLength(value: number | string | undefined): string | undefined {
  if (value === undefined) return undefined;
  return typeof value === "number" ? `${value}px` : value;
}

export function Skeleton({
  variant = "rect",
  width = "100%",
  height,
  className = "",
  "aria-label": ariaLabel = "Loading",
}: SkeletonProps): ReactNode {
  const radius =
    variant === "circle"
      ? "rounded-full"
      : variant === "text"
      ? "rounded-sm"
      : "rounded-md";

  const resolvedHeight =
    height ?? (variant === "text" ? "1rem" : variant === "circle" ? width : "100%");

  const style: CSSProperties = {
    width: toCssLength(width),
    height: toCssLength(resolvedHeight),
  };

  return (
    <span
      role="status"
      aria-busy="true"
      aria-label={ariaLabel}
      style={style}
      className={[
        // Display + sizing
        "inline-block align-middle",
        radius,
        // Background gradient + animated translation
        "bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100",
        "bg-[length:200%_100%]",
        // Animation — disabled under prefers-reduced-motion via the
        // motion-safe variant.
        "motion-safe:animate-[skeleton-shimmer_1.4s_ease-in-out_infinite]",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    />
  );
}

/**
 * Convenience: render N stacked text skeletons of varying widths.
 * Use for paragraph or list-row placeholders.
 */
export function SkeletonText({
  lines = 3,
  className = "",
}: {
  lines?: number;
  className?: string;
}): ReactNode {
  // Vary widths so the placeholder feels like real text, not a bar.
  const widths = ["100%", "92%", "78%", "84%", "70%"];
  return (
    <span
      role="status"
      aria-busy="true"
      aria-label="Loading"
      className={["flex flex-col gap-2", className].join(" ")}
    >
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton
          key={i}
          variant="text"
          width={widths[i % widths.length]}
          aria-label="Loading"
        />
      ))}
    </span>
  );
}
